<template>
    <section class="offer-presets-list">
        <offer-presets-toolbar
            :price-tags="priceTags"
            :creation-sources="availableCreationSources"
            @filter="filters = $event"
            @create="createPreset"
        />
        <div class="offer-presets-list__grid">
            <promo-ag-grid
                :row-data="filteredPresets"
                :column-defs="columnDefs"
                :grid-options="gridOptions"
                :default-col-def="defaultColDef"
                grid-class="ag-theme-custom__product-details"
                grid-style="width: 100%; height: 100%;"
            />
        </div>
        <details v-if="deletedSystemPresets.length" class="offer-presets-list__deleted">
            <summary
                >{{ $t('admin.offerPresets.table.deletedSystems') }} ({{
                    deletedSystemPresets.length
                }})</summary
            >
            <ul>
                <li v-for="template in deletedSystemPresets" :key="template">
                    {{ getPresetName({ offerTemplate: template, name: template }) }}
                    <v-btn size="small" variant="text" @click="restorePreset(template)">{{
                        $t('admin.offerPresets.actions.restore')
                    }}</v-btn>
                </li>
            </ul>
        </details>
        <confirm-dialog
            ref="deleteDialog"
            :has-activator="false"
            :question-text="
                $t('admin.offerPresets.confirmDelete', {
                    name: presetToDelete ? getPresetName(presetToDelete) : '',
                })
            "
            :warning-text="$t('admin.offerPresets.confirmDeleteWarning')"
            :action-text="$t('admin.offerPresets.actions.delete')"
            @confirm="confirmDelete"
            @close="presetToDelete = null"
        />
        <price-tag-preview-dialog
            v-if="previewPreset"
            ref="previewDialog"
            :preset="previewPreset"
            :offer-mechanic="previewPreset.templateStructure"
            :display-values="previewPreset.displayValues"
            :context-source="contextSource"
        />
        <offer-preset-wizard ref="wizard" @saved="reload" />
    </section>
</template>

<script setup>
import { computed, markRaw, nextTick, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import creationSourceOptions from './creation-sources';
import OfferPresetActions from '@/js/components/promo-ag-grid/offer-presets-actions.vue';
import { toSentenceCase } from '@/js/utils/string-utils';
import { getOfferTemplateLabel, getPriceTagLabel } from '@/js/utils/offer-template-label';
import i18n from '@/js/vue-i18n';

const store = useStore();
const filters = ref({ search: '', source: 'all', tag: 'all' });
const wizard = ref(null);
const previewDialog = ref(null);
const previewPreset = ref(null);
const deletedSystemPresets = ref([]);
const deleteDialog = ref(null);
const presetToDelete = ref(null);
const presets = computed(() => store.getters['offerMechanicPresets/adminPresets']);
const priceTags = computed(() => store.state.priceTags.priceTags);
const availableCreationSources = computed(() =>
    creationSourceOptions.filter(source => source.isEnabled(store.state.clientConfig.toggleLogic))
);
const tagsById = computed(() => store.getters['priceTags/priceTagsKeyed']);
const currencySymbol = computed(() => store.getters['clientConfig/currencySymbol'] || '');
const getTagName = id =>
    getPriceTagLabel({ id, name: tagsById.value[id]?.name, currencySymbol: currencySymbol.value });
const getPresetName = preset =>
    getOfferTemplateLabel({
        key: preset.offerTemplate,
        name: preset.name,
        currencySymbol: currencySymbol.value,
    });
const gridOptions = markRaw({
    rowHeight: 40,
    headerHeight: 44,
    paginationAutoPageSize: true,
    pagination: true,
});
const defaultColDef = {
    suppressMovable: true,
    sortable: true,
    unSortIcon: true,
    flex: 1,
    resizable: true,
    filter: true,
    minWidth: 120,
    menuTabs: ['filterMenuTab'],
};
const contextSource = {
    products: [],
    productOfferGroups: [],
    startDate: null,
    endDate: null,
    moment: undefined,
    currencySymbol: '',
    loyaltyKey: null,
    transformContext: {},
};
const filteredPresets = computed(() => {
    const term = (filters.value.search || '').trim().toLowerCase();
    return presets.value.filter(preset => {
        const matchesTerm =
            !term ||
            `${getPresetName(preset)} ${preset.name} ${preset.description}`
                .toLowerCase()
                .includes(term);
        return (
            matchesTerm &&
            (filters.value.source === 'all' || preset.source === filters.value.source) &&
            (filters.value.tag === 'all' || preset.priceTagId === filters.value.tag)
        );
    });
});
const reload = async () => {
    const [, , deletedResponse] = await Promise.all([
        store.dispatch('offerMechanicPresets/fetchOfferMechanicPresets'),
        store.dispatch('priceTags/fetchPriceTags'),
        axios.get('/api/offer-mechanic-presets/deleted-system-presets'),
    ]);
    deletedSystemPresets.value = deletedResponse.data;
};
const createPreset = source => wizard.value.open({ source });
function editPreset(preset) {
    wizard.value.open({ preset });
}

function duplicatePreset(preset) {
    const source = preset.creationSource === 'mechanicForm' ? 'mechanicForm' : 'priceTag';
    wizard.value.open({
        source,
        preset: {
            ...preset,
            _id: null,
            creationSource: source,
            name: `${preset.name} copy`,
            source: 'custom',
            isModified: false,
        },
    });
}

async function preview(preset) {
    previewPreset.value = {
        ...preset,
        priceTag: tagsById.value[preset.priceTagId],
    };
    await nextTick();
    previewDialog.value?.open();
}

async function resetPreset(preset) {
    await axios.post(`/api/offer-mechanic-presets/${preset._id}/reset`);
    await reload();
}
const restorePreset = async offerTemplate => {
    await axios.post(
        `/api/offer-mechanic-presets/system/${encodeURIComponent(offerTemplate)}/reset`
    );
    await reload();
};
function deletePreset(preset) {
    presetToDelete.value = preset;
    deleteDialog.value.open();
}
const confirmDelete = async () => {
    const preset = presetToDelete.value;
    if (!preset) return;
    await axios.delete(`/api/offer-mechanic-presets/${preset._id}`);
    await reload();
};

const columnDefs = computed(() => [
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.name')),
        field: 'name',
        valueGetter: params => getPresetName(params.data),
        minWidth: 220,
        flex: 2,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.tag')),
        field: 'priceTagId',
        valueGetter: params => getTagName(params.data.priceTagId),
        minWidth: 180,
        flex: 1.5,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.source')),
        field: 'source',
        valueFormatter: params => toSentenceCase(params.value || ''),
        minWidth: 110,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.createdFrom')),
        field: 'creationSource',
        valueFormatter: ({ value }) => {
            const key = {
                system: 'system',
                priceTag: 'fromPriceTag',
                mechanicForm: 'fromMechanicForm',
            }[value];
            return key ? toSentenceCase(i18n.global.t(`admin.offerPresets.${key}`)) : value;
        },
        minWidth: 140,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.modified')),
        field: 'isModified',
        // ag-grid renders boolean columns as checkboxes unless the data type is inferred off.
        cellDataType: false,
        valueFormatter: ({ value }) =>
            value ? toSentenceCase(i18n.global.t('admin.offerPresets.table.modifiedYes')) : '',
        maxWidth: 120,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.planner')),
        field: 'isHiddenInPlanner',
        cellDataType: false,
        valueFormatter: ({ value }) =>
            toSentenceCase(
                i18n.global.t(
                    value ? 'admin.offerPresets.wizard.hidden' : 'admin.offerPresets.wizard.visible'
                )
            ),
        minWidth: 120,
    },
    {
        headerName: toSentenceCase(i18n.global.t('admin.offerPresets.table.order')),
        field: 'sortOrder',
        maxWidth: 100,
    },
    {
        headerName: '',
        cellRenderer: markRaw(OfferPresetActions),
        cellRendererParams: {
            onPreview: preset => preview(preset),
            onEdit: preset => editPreset(preset),
            onDuplicate: preset => duplicatePreset(preset),
            onReset: preset => resetPreset(preset),
            onDelete: preset => deletePreset(preset),
        },
        sortable: false,
        filter: false,
        resizable: false,
        minWidth: 56,
        maxWidth: 56,
        flex: 0,
        cellClass: 'flex-cell-center',
    },
]);

onMounted(reload);
</script>

<style scoped lang="scss">
.offer-presets-list {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: 1.2rem;
    min-height: 0;

    &__grid {
        flex: 1 1 0;
        min-height: 32rem;
    }

    &__deleted {
        flex: 0 0 auto;
    }

    :deep(.flex-cell-center) {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    // 15% larger than the shared grid theme (1.2rem rows, 1.1rem headers, 1.3rem paging).
    :deep(.ag-theme-custom) {
        .ag-row {
            font-size: 1.38rem;
        }

        .ag-header-cell-text {
            font-size: 1.27rem;
            line-height: 1.6rem;
        }

        .ag-paging-panel {
            font-size: 1.5rem;
        }
    }
}
</style>
